import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiUtils } from '../utils/api';

export default function ProductPageEditor() {
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(null);

  const [heroOpen, setHeroOpen] = useState(false);

  const [hero, setHero] = useState({
    badge: '✨ Premium Product Collection',
    headingLine1: 'Explore Our',
    headingLine2: 'Premium Products',
    description: 'Discover premium quality products across multiple categories.',
    features: 'Global Quality • Trusted Supply',
    isActive: true,
  });

  const heroSectionId = useMemo(() => {
    const existing = (page?.sections || []).find(s => !s.isGlobal && s.sectionType === 'hero');
    return existing?.sectionId || 'hero-1';
  }, [page]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const res = await apiUtils.cms.getPage('products');

      setLoading(false);
      if (!res.success) {
        toast.error(res.error || 'Failed to load products page');
        // Still allow editing/saving by initializing an empty page model
        setPage({ pageName: 'products', metaTitle: '', metaDescription: '', sections: [] });
        return;
      }

      setPage(res.data);

      const heroSection = (res.data?.sections || []).find(s => s.sectionType === 'hero' && !s.isGlobal);
      const c = heroSection?.content || heroSection || {};
      setHero({
        badge: c.badge || '✨ Premium Product Collection',
        headingLine1: c.headingLine1 || 'Explore Our',
        headingLine2: c.headingLine2 || 'Premium Products',
        description: c.description || '',
        features: c.features || 'Global Quality • Trusted Supply',
        isActive: heroSection?.isActive ?? true,
      });
    })();
  }, []);

  const onChange = (key) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setHero(prev => ({ ...prev, [key]: value }));
  };

  const onSave = async () => {
    const safePage = page || { metaTitle: '', metaDescription: '', sections: [] };

    setLoading(true);
    try {
      // Build new sections array: upsert hero section, keep others unchanged.
      const existingSections = safePage.sections || [];
      const existingHeroIndex = existingSections.findIndex(s => !s.isGlobal && s.sectionType === 'hero');

      const nextHeroSection = {
        sectionId: heroSectionId,
        sectionType: 'hero',
        isGlobal: false,
        order: existingHeroIndex >= 0 ? existingSections[existingHeroIndex].order : 0,
        isActive: !!hero.isActive,
        content: {
          badge: hero.badge,
          headingLine1: hero.headingLine1,
          headingLine2: hero.headingLine2,
          description: hero.description,
          features: hero.features,
        },
        visualSettings: {},
      };

      const upsert = (arr, predicate, section) => {
        const idx = arr.findIndex(predicate);
        if (idx >= 0) return arr.map((s, i) => (i === idx ? section : s));
        return [section, ...arr];
      };

      let nextSections = upsert(existingSections, (s) => !s.isGlobal && s.sectionType === 'hero', nextHeroSection);

      // Normalize sections to match backend validation exactly.
      const normalizedSections = nextSections
        .slice()
        .sort((a, b) => (a.order ?? 999) - (b.order ?? 999))
        .map((s, idx) => {
          const isGlobal = !!s.isGlobal;
          const base = {
            sectionId: String(s.sectionId),
            order: idx,
            isActive: s.isActive !== false,
            isGlobal,
          };

          if (isGlobal) {
            return {
              ...base,
              globalSectionRef: String(s.globalSectionRef || '').toLowerCase(),
            };
          }

          return {
            ...base,
            sectionType: String(s.sectionType || '').toLowerCase(),
            content: s.content || {},
            visualSettings: s.visualSettings || {},
          };
        })
        // Drop invalid global refs to avoid 400s.
        .filter((s) => (s.isGlobal ? !!s.globalSectionRef : !!s.sectionType));

      const res = await apiUtils.cms.updatePage('products', {
        metaTitle: safePage.metaTitle || '',
        metaDescription: safePage.metaDescription || '',
        sections: normalizedSections,
      });

      if (!res.success) {
        toast.error(res.error || 'Failed to save');
        return;
      }

      setPage(res.data);
      toast.success('Products page saved');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Products</h1>
        </div>
        <button
          onClick={onSave}
          disabled={loading}
          className="px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 disabled:opacity-60"
        >
          {loading ? 'Saving…' : 'Save'}
        </button>
      </div>

      <div className="space-y-4">
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <button
            type="button"
            onClick={() => setHeroOpen(v => !v)}
            className="w-full flex items-center justify-between p-4 hover:bg-gray-50"
            aria-expanded={heroOpen}
          >
            <div className="text-left">
              <div className="text-sm font-semibold text-gray-900">Hero</div>
              <div className="text-xs text-gray-500">sectionId: <code>{heroSectionId}</code></div>
            </div>
            <ChevronDown className={`h-5 w-5 text-gray-500 transition-transform ${heroOpen ? 'rotate-180' : ''}`} />
          </button>

          {heroOpen && (
            <div className="p-4 border-t border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <div className="text-xs text-gray-500">Edit fields below</div>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={!!hero.isActive} onChange={onChange('isActive')} />
                  Active
                </label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Badge Text</label>
                  <input value={hero.badge} onChange={onChange('badge')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                  <p className="text-xs text-gray-500 mt-1">e.g., "✨ Premium Product Collection"</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Heading Line 1</label>
                  <input value={hero.headingLine1} onChange={onChange('headingLine1')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Heading Line 2 (Gradient)</label>
                  <input value={hero.headingLine2} onChange={onChange('headingLine2')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea value={hero.description} onChange={onChange('description')} rows={4} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Features (separated by •)</label>
                  <input value={hero.features} onChange={onChange('features')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                  <p className="text-xs text-gray-500 mt-1">e.g., "Global Quality • Trusted Supply"</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
